import type { ObservationKind, Status, StatusOverride } from '../types';
import { deriveStatus } from './status';

/**
 * INV-2, INV-9, INV-14 — counts, and the fold that reproduces them.
 *
 * `cachedCount` "equals the count of its `increment` observations at all times after any
 * transaction commits" (INV-2). This module is the only place that arithmetic exists, in two
 * shapes that must agree by construction:
 *
 *   - the **incremental** step the live handlers apply (`applyLiveIncrement`, `revertIncrement`),
 *     computing `prev ± 1` from a row read inside the transaction;
 *   - the **fold** (`replay` / `reconcile`), which recomputes from the Observation log.
 *
 * Design §4 makes them the same function for INV-9's purposes: `reconcile` *is* `replay`, used by
 * import, migration and the §13.6 drift assertion. Equivalence is definitional, not hoped for.
 *
 * ── The one asymmetry, and why it is not a bug ───────────────────────────────
 * LIVE-11 says tapping a dismissed tile increments it **and clears the dismissal**. INV-9 says
 * replay holds user-set overrides **constant**. Those are not in conflict, but they are easy to
 * merge into a live bug: an override is not an Observation, so the log cannot say when it was set
 * relative to the increments, and a fold that cleared overrides would silently un-disprove every
 * hypothesis on import — re-offering, under PRE-5 after a phone-loss restore, exactly the patterns
 * the user had rejected (design §3.3). So:
 *
 *   `applyLiveIncrement` clears the override — it is the tap path, where LIVE-11 applies.
 *   `applyObservation`   preserves it       — it is the fold, where INV-9 applies.
 *
 * Two functions, each named for its path, rather than one with a flag.
 */

/**
 * The only Observation fields a count depends on.
 *
 * `MatchNote` is **not** representable here (INV-14): notes are mutable and are excluded from
 * replay, so keeping them out of the input type is what makes "never affects `cachedCount`" a
 * property of the type system rather than a convention.
 */
export interface CountableObservation {
  readonly hypothesisId: string | null;
  readonly kind: ObservationKind;
  readonly createdAt: number;
}

/**
 * The count-derived state of one hypothesis — exactly the fields a commit writes.
 *
 * `statusOverride` is carried because `status` cannot be derived without it (design §3.3), but it
 * is never *computed* here: the dismissal dimension is user-set in every path except LIVE-11's
 * clear-on-increment.
 */
export interface CountState {
  readonly cachedCount: number;
  /** Design §3.2 — `createdAt` of the newest surviving `increment`. Backs LIVE-6 with no timer. */
  readonly lastIncrementTs: number | null;
  readonly statusOverride: StatusOverride;
  /** Materialized by the one expression in `status.ts`. Never written anywhere else. */
  readonly status: Status;
}

/** A hypothesis with no observations yet. `carriedStatus` (PRE-5) does not seed a count. */
export function initialCountState(statusOverride: StatusOverride = null): CountState {
  return {
    cachedCount: 0,
    lastIncrementTs: null,
    statusOverride,
    status: deriveStatus(statusOverride, 0),
  };
}

/** Rebuilds the derived `status` field so it can never drift from `(override, count)`. */
function withDerivedStatus(
  cachedCount: number,
  lastIncrementTs: number | null,
  statusOverride: StatusOverride,
): CountState {
  return {
    cachedCount,
    lastIncrementTs,
    statusOverride,
    status: deriveStatus(statusOverride, cachedCount),
  };
}

/**
 * The **fold step**. Counts an `increment`; ignores everything else; preserves the override.
 *
 * A `voiceNote` or a `flag` is an Observation but not evidence — INV-2 counts increments only, and
 * a flag additionally carries `hypothesisId = null` (FLAG-2), so it belongs to no hypothesis at
 * all. Both are rejected here rather than only at the call site.
 */
export function applyObservation(prev: CountState, obs: CountableObservation): CountState {
  if (obs.kind !== 'increment' || obs.hypothesisId === null) return prev;
  const lastIncrementTs =
    prev.lastIncrementTs === null ? obs.createdAt : Math.max(prev.lastIncrementTs, obs.createdAt);
  return withDerivedStatus(prev.cachedCount + 1, lastIncrementTs, prev.statusOverride);
}

/**
 * The **live tap path** (LIVE-2, LIVE-11). Increments, and clears any dismissal.
 *
 * LIVE-11: "Tapping a `disproved` or `invalidated` tile increments it and returns it to `tracking`
 * or `confirmed` per its resulting count. The tap MUST NOT be silently ignored, and MUST NOT
 * require the user to first un-disprove via a separate gesture." Clearing the override and letting
 * `deriveStatus` re-answer from the count is that whole behavior — there is no second branch.
 *
 * `boardPosition` is not in this type, so an increment structurally cannot move a tile (INV-4).
 */
export function applyLiveIncrement(prev: CountState, createdAt: number): CountState {
  const lastIncrementTs =
    prev.lastIncrementTs === null ? createdAt : Math.max(prev.lastIncrementTs, createdAt);
  return withDerivedStatus(prev.cachedCount + 1, lastIncrementTs, null);
}

/**
 * LIVE-6 — one undo: drop a count and re-anchor the window on the newest *surviving* increment.
 *
 * @param newLastIncrementTs `createdAt` of the newest increment that remains after the deletion,
 *   or `null` if none remains. Re-anchoring is what makes repeat-undo judge each surviving
 *   increment against **its own** eight seconds (design §5.4).
 *
 * The override is preserved: undo reverses evidence, and a user's dismissal is not evidence.
 */
export function revertIncrement(prev: CountState, newLastIncrementTs: number | null): CountState {
  return withDerivedStatus(
    Math.max(0, prev.cachedCount - 1),
    newLastIncrementTs,
    prev.statusOverride,
  );
}

/**
 * INV-3 — set or clear the dismissal dimension. The count is untouched: it was masked, never
 * destroyed, so clearing returns the hypothesis to `confirmed` or `tracking` per its current count.
 */
export function setStatusOverride(prev: CountState, statusOverride: StatusOverride): CountState {
  return withDerivedStatus(prev.cachedCount, prev.lastIncrementTs, statusOverride);
}

/**
 * INV-9 — replay the Observation log against a clean state.
 *
 * Reproduces **counts and count-derived status**, holding user-set overrides constant, which is
 * exactly what revision 2 narrowed INV-9 to. Nothing else is reproduced and nothing else is
 * touched.
 *
 * A hypothesis present in `overrides` with no observations still appears in the result, at count 0
 * — otherwise reconciling an import would drop every hypothesis the user had dismissed before it
 * ever accumulated evidence.
 *
 * The fold is **order-independent**: the count is a sum and `lastIncrementTs` is a maximum, so a
 * shuffled log produces an identical result. That is what lets `reconcile` run against rows read in
 * index order without depending on the index.
 *
 * @param overrides the *current* dismissal of each known hypothesis — row state, not log state.
 */
export function replay(
  observations: readonly CountableObservation[],
  overrides: ReadonlyMap<string, StatusOverride>,
): Map<string, CountState> {
  const result = new Map<string, CountState>();

  for (const [hypothesisId, override] of overrides) {
    result.set(hypothesisId, initialCountState(override));
  }

  for (const obs of observations) {
    if (obs.kind !== 'increment' || obs.hypothesisId === null) continue;
    const id = obs.hypothesisId;
    const prev = result.get(id) ?? initialCountState(overrides.get(id) ?? null);
    result.set(id, applyObservation(prev, obs));
  }

  return result;
}

/**
 * Design §4 — the single reconciler for BAK-2 import, NFR-7 migration and the §13.6 drift
 * assertion. It is `replay`, not a second implementation, so INV-9's equivalence is definitional.
 */
export const reconcile = replay;
